import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  Trash2,
  FileText,
  Copy,
  Power,
  MoreHorizontal
} from "feather-icons-react/build/IconComponents";
import AddMenu from "../../../core/modals/menus/addmenu";
import axios from "axios";
import Swal from "sweetalert2";
import { base_api_url } from "../../../environment.jsx";
import withReactContent from "sweetalert2-react-content";
import { all_routes } from "../../../Router/all_routes";

const MenuCard = ({ title, description, isActive, menuId, menu, nbArticles, onRefresh }) => {
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const [showEditMenuModal, setShowEditMenuModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // get the current menu
  const currentMenu = menu.find(m => m.id_menu === menuId);

  const handleViewArticles = () => {
    navigate(`${all_routes.articlesNew}?menuId=${menuId}`);
  };

  // Handle status toggle
  const handleStatusToggle = async (event) => {
    const newStatus = event.target.checked;
    setUpdatingStatus(true);

    try {
      const token = JSON.parse(localStorage.getItem("user"))?.access_token;

      // Find the current menu data
      const currentMenu = menu.find(m => m.id_menu === menuId);
      if (!currentMenu) {
        throw new Error("Menu not found");
      }

      // Prepare the update data with the new status
      const updateData = {
        nom: currentMenu.nom,
        description: currentMenu.description,
        disponible: newStatus,
        heure_debut: currentMenu.heure_debut || null,
        heure_fin: currentMenu.heure_fin || null,
        id_restaurant: currentMenu.id_restaurant
      };

      console.log("Updating menu status:", updateData);

      const response = await axios.put(
        `${base_api_url}api/menuDispo/${menuId}`,
        updateData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Status update response:", response.data);

      // Show success message
      MySwal.fire({
        icon: "success",
        // title: "Statut mis à jour !",
        text: `Le menu a été ${newStatus ? 'activé' : 'désactivé'} avec succès.`,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        customClass: {
          popup: "pixel-toast",
        },
      });

      // Trigger refresh in parent component
      if (onRefresh) {
        onRefresh();
      }

    } catch (error) {
      console.error("Error updating menu status:", error);

      MySwal.fire({
        icon: "error",
        // title: "Erreur",
        text: "Impossible de mettre à jour le statut du menu.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: "pixel-toast",
        },
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Fonction pour gérer l'édition d'un menu
  const handleEditMenus = (menuId) => {
    console.log("Menu data being passed to modal:", menuId);
    const menuToEdit = menu.find(m => m.id_menu === menuId);
    if (!menuToEdit) {
      console.error("Menu not found for ID:", menuId);
      return;
    }
    setEditingMenu(menuToEdit);
    setShowEditMenuModal(true);
  };

  // Fonction pour mettre à jour un menu
  const handleUpdateSuccess = () => {
    // Close modal
    setShowEditMenuModal(false);
    setEditingMenu(null);

    // Trigger refresh in parent component
    if (onRefresh) {
      console.log("Calling onRefresh to update menu data");
      onRefresh();
    }
  };

  /**
   * Supprime le menu après confirmation
   */
  const handleDeleteMenu = async () => {
    const result = await MySwal.fire({
      title: 'Êtes-vous sûr ?',
      text: "Cette action est irréversible !",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f97316',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        const token = JSON.parse(localStorage.getItem("user"))?.access_token;
        await axios.put(`${base_api_url}api/menu/${menuId}`, { statut: 0 }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        MySwal.fire({
          icon: 'success',
          text: 'Menu supprimé avec succès',
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 3000,
          customClass: { popup: "pixel-toast" }
        });

        if (onRefresh) onRefresh();
      } catch (error) {
        MySwal.fire({
          icon: 'error',
          text: 'Erreur lors de la suppression',
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 3000,
          customClass: { popup: "pixel-toast" }
        });
      }
    }
  };

  return (
    <div className="menu-card">
      <div className="menu-card-header">
        <h4>{title}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {updatingStatus && (
            <span
              className="spinner-border spinner-border-sm text-primary"
              style={{ width: '16px', height: '16px' }}
            ></span>
          )}
          <label className="switch">
            <input
              type="checkbox"
              checked={isActive}
              onChange={handleStatusToggle}
              disabled={updatingStatus}
            />
            <span className="slider round"></span>
          </label>
          <button 
            className="header-edit-btn-raw" 
            title="Modifier" 
            onClick={() => handleEditMenus(menuId)}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: '#64748b', 
              cursor: 'pointer',
              padding: '0',
              marginLeft: '8px',
              display: 'flex',
              alignItems: 'center',
              outline: 'none'
            }}
          >
            <Edit size={18} />
          </button>
        </div>
      </div>

      <div className="days-wrapper">
        <span className={`day-circle ${currentMenu?.dispo_lun ? 'active' : ''}`}>L</span>
        <span className={`day-circle ${currentMenu?.dispo_mar ? 'active' : ''}`}>M</span>
        <span className={`day-circle ${currentMenu?.dispo_mer ? 'active' : ''}`}>M</span>
        <span className={`day-circle ${currentMenu?.dispo_jeu ? 'active' : ''}`}>J</span>
        <span className={`day-circle ${currentMenu?.dispo_ven ? 'active' : ''}`}>V</span>
        <span className={`day-circle ${currentMenu?.dispo_sam ? 'active' : ''}`}>S</span>
        <span className={`day-circle ${currentMenu?.dispo_dim ? 'active' : ''}`}>D</span>
      </div>

      <p className="menu-card-desc">{description || "Aucune description fournie"}</p>

      <hr className="menu-card-hr" />

      <div className="menu-card-footer-modern">
        <div className="footer-left-info">
          <FileText size={20} color="#64748b" />
          <span className="articles-included-text">
            {nbArticles || 0} article{(nbArticles || 0) > 1 ? 's' : ''} inclus
          </span>
        </div>

        <div className="footer-right-actions">
          <button className="btn-view-articles-modern" onClick={handleViewArticles}>
            Voir les articles
          </button>

          <div className="menu-dots-dropdown">
            <button className="action-button-dots-simple" title="Plus d'actions">
              <MoreHorizontal size={20} color="#64748b" />
            </button>
            <div className="dropdown-menu-modern">
               <div className="dropdown-item-modern" onClick={() => {}}>
                 <Copy size={16} />
                 <span>Dupliquer</span>
               </div>
               <div className="dropdown-item-modern" onClick={() => handleStatusToggle({ target: { checked: !isActive } })}>
                 <Power size={16} />
                 <span>{isActive ? "Désactiver" : "Activer"}</span>
               </div>
               <div className="dropdown-item-modern delete" onClick={handleDeleteMenu}>
                 <Trash2 size={16} />
                 <span>Supprimer</span>
               </div>
            </div>
          </div>
        </div>
      </div>

      {showEditMenuModal && editingMenu && (
        <AddMenu
          show={showEditMenuModal}
          editMode={true}
          menuData={editingMenu}
          onFormSubmit={handleUpdateSuccess}
          onClose={() => {
            setShowEditMenuModal(false);
            setEditingMenu(null);
          }}
        />
      )}
    </div>
  );
};

MenuCard.propTypes = {
  title: PropTypes.string.isRequired,
  detail: PropTypes.string,
  description: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  menuId: PropTypes.number.isRequired,
  menu: PropTypes.array.isRequired,
  nbArticles: PropTypes.number,
  onRefresh: PropTypes.func
};

export default MenuCard;